import { decode } from 'base-64';

/**
 * Standard OIDC claims from the Curity id_token.
 * Only `sub` is required. Profile claims (`given_name`, `family_name`, `email`)
 * are optional — the test Curity environment omits them, embedding the user's
 * email address in `sub` and `preferred_username` instead.
 */
export interface IdTokenPayload {
  sub: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  preferred_username?: string;
  iss?: string;
  aud?: string | string[];
  iat?: number;
  exp?: number;
  nonce?: string;
  roles?: string[];
  providerUserId?: number;
}

/**
 * Identity derived from the id_token — used for display in navbar, profile, etc.
 */
export interface IdTokenIdentity {
  sub: string;
  firstName: string;
  lastName: string;
  email: string;
  displayName: string;
  roles: string[];
  providerUserId?: number;
}

const isIdTokenPayload = (value: unknown): value is IdTokenPayload => {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.sub === 'string';
};

/**
 * Decodes the id_token JWT and extracts identity fields.
 * Falls back to `preferred_username` or `sub` for email when the `email`
 * claim is absent (e.g. Curity test environment). Name fields default to
 * empty string when profile claims are not present.
 * WARNING: This performs NO signature verification. The returned values
 * must only be used for display purposes, never for authorization.
 */
export function decodeIdToken(idToken: string): IdTokenIdentity {
  const payload = jwtDecode(idToken);
  if (!isIdTokenPayload(payload)) {
    throw new Error('id_token missing required sub claim');
  }
  const firstName = payload.given_name ?? '';
  const lastName = payload.family_name ?? '';
  const email = payload.email ?? payload.preferred_username ?? payload.sub;
  const displayName = `${firstName} ${lastName}`.trim() || email;
  return {
    sub: payload.sub,
    firstName,
    lastName,
    email,
    displayName,
    roles: payload.roles ?? [],
    providerUserId: payload.providerUserId,
  };
}

/**
 * Normalizes a base64url-encoded string to standard base64.
 * JWTs use base64url encoding (RFC 7515) which replaces + with - and / with _.
 */
function base64UrlToBase64(str: string): string {
  return str.replace(/-/g, '+').replace(/_/g, '/') + '=='.slice(0, (4 - (str.length % 4)) % 4);
}

function assertSegment(
  encodedData: string | undefined
): asserts encodedData is string {
  if (!encodedData) {
    throw new Error(
      `Invalid token specified: invalid base64 '${encodedData}'`
    );
  }
}

function decodeSegment(encodedData: string | undefined): string {
  try {
    assertSegment(encodedData);
    const binaryString = decode(base64UrlToBase64(encodedData));
    return decodeURIComponent(
      binaryString
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    throw new Error(
      `Invalid token specified: invalid base64 for (${message})`
    );
  }
}

function parseJson(decodedData: string): unknown {
  try {
    return JSON.parse(decodedData) as unknown;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    throw new Error(`Invalid token specified: invalid json for (${message})`);
  }
}

export function jwtDecode(token: string, withHeader = true): unknown {
  const [header, data] = token.split('.');
  const encodedData = withHeader ? data : header;
  return parseJson(decodeSegment(encodedData));
}

/**
 * Decodes the id_token JWT payload and returns it with standard OIDC claims.
 * Used for nonce verification after token exchange.
 * WARNING: No signature verification — use only for replay-guard checks.
 */
export function decodeIdTokenPayload(idToken: string): IdTokenPayload {
  const payload = jwtDecode(idToken);
  if (!isIdTokenPayload(payload)) {
    throw new Error('id_token missing required sub claim');
  }
  return payload;
}
